'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './flow.module.css';
import type { FlowStep, FlowValues } from './types';

/**
 * The field for one step.
 *
 * Split out of the engine so each kind of answer can behave the way that kind
 * of answer should: text takes Enter to continue, a list or a card is a
 * decision and moves on by itself, and a filtered list focuses its search box
 * rather than the first option.
 */
export function FlowField({
  step,
  value,
  values,
  invalid,
  describedBy,
  onChange,
  onEnter,
  onPick,
}: {
  step: FlowStep;
  value: string;
  /**
   * Every answer so far, for the kinds that read more than their own.
   *
   * A connect step is the case: what it shows once it has worked is the account
   * it proved, which its `display` renders out of the values the connect wrote
   * — the handle, the platform, the channel address. See `FlowConnectStep`.
   */
  values: FlowValues;
  invalid: boolean;
  describedBy: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  onPick: (value: string) => void;
}): React.ReactElement {
  switch (step.kind) {
    /*
      Nothing at all until it has worked, and that is the whole design.

      The control for this step is its primary button, which says `Connect
      Twitch` rather than `Next` — see `FlowConnectStep` for why there is one
      control and not two. So there is no field here to draw; once the answer is
      in, what belongs on screen is what was proved.
    */
    case 'connect':
      return value.trim() === '' ? (
        <></>
      ) : (
        <p className={styles.connected} role="status">
          {step.display?.(value, values) ?? value}
        </p>
      );
    case 'choice':
      return <ChoiceField step={step} value={value} describedBy={describedBy} onPick={onPick} />;
    case 'cards':
      return (
        <div className={styles.cards} role="group" aria-labelledby={describedBy}>
          {step.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={styles.card}
              aria-pressed={value === option.value}
              onClick={() => onPick(option.value)}
            >
              <span className={styles.cardLabel}>{option.label}</span>
              {option.description && (
                <span className={styles.cardDescription}>{option.description}</span>
              )}
            </button>
          ))}
        </div>
      );
    case 'number':
      return (
        <TextLikeField
          step={step}
          value={value}
          invalid={invalid}
          describedBy={describedBy}
          onChange={onChange}
          onEnter={onEnter}
          numeric
        />
      );
    case 'text':
    default:
      return (
        <TextLikeField
          step={step}
          value={value}
          invalid={invalid}
          describedBy={describedBy}
          onChange={onChange}
          onEnter={onEnter}
          numeric={false}
          multiline={step.kind === 'text' && step.multiline === true}
        />
      );
  }
}

/** Text, number and prefixed variants, which behave identically apart from the keyboard. */
function TextLikeField({
  step,
  value,
  invalid,
  describedBy,
  onChange,
  onEnter,
  numeric,
  multiline = false,
}: {
  step: FlowStep;
  value: string;
  invalid: boolean;
  describedBy: string;
  onChange: (value: string) => void;
  onEnter: () => void;
  numeric: boolean;
  multiline?: boolean;
}): React.ReactElement {
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Focus on arrival, so the whole flow can be completed from the keyboard
  // without ever reaching for the mouse.
  useEffect(() => {
    ref.current?.focus();
  }, [step.name]);

  const prefix = 'prefix' in step ? step.prefix : undefined;
  const suffix = 'suffix' in step ? step.suffix : undefined;

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    // In a textarea, Enter is a newline and Ctrl/Cmd+Enter continues. Anywhere
    // else Enter continues, which is the whole keyboard contract of this shape
    // of form.
    if (multiline && !(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    onEnter();
  }

  return (
    <div className={styles.field} data-invalid={invalid}>
      {prefix && <span className={styles.affix}>{prefix}</span>}
      {multiline ? (
        <textarea
          ref={ref as React.Ref<HTMLTextAreaElement>}
          className={styles.textarea}
          value={value}
          aria-labelledby={describedBy}
          aria-invalid={invalid}
          maxLength={'maxLength' in step ? step.maxLength : undefined}
          placeholder={'placeholder' in step ? step.placeholder : undefined}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          className={`${styles.input} ${numeric ? styles.inputMono : ''}`.trim()}
          value={value}
          aria-labelledby={describedBy}
          aria-invalid={invalid}
          inputMode={numeric ? 'decimal' : undefined}
          autoComplete="off"
          autoCapitalize={
            'autoCapitalize' in step && step.autoCapitalize ? step.autoCapitalize : undefined
          }
          maxLength={'maxLength' in step ? step.maxLength : undefined}
          placeholder={'placeholder' in step ? step.placeholder : undefined}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      )}
      {suffix && <span className={styles.affix}>{suffix}</span>}
    </div>
  );
}

/**
 * A filtered list.
 *
 * The country picker, in practice. The pre-selected answer — on sign-up, the
 * country `GET /v1/sign-in-options` guessed from the connection — is named
 * above the list rather than being silently scrolled to, because a value that
 * was chosen for somebody should be visible as a suggestion they can decline,
 * not as a decision already taken.
 *
 * **Which is why an empty filter box does not let Enter pick the first row.**
 * Enter is how somebody accepts what is already on screen, and a list nobody
 * has typed into has no "first match" — only a first country, alphabetically,
 * which is Australia on the payout list and, on the sign-up list, whichever
 * country sorts first in the reader's own language. Answering a keypress meant
 * as *yes, that one* with a country from the top of the alphabet is the worst
 * kind of wrong: silent, plausible, and past before anybody sees it.
 */
function ChoiceField({
  step,
  value,
  describedBy,
  onPick,
}: {
  step: Extract<FlowStep, { kind: 'choice' }>;
  value: string;
  describedBy: string;
  onPick: (value: string) => void;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, [step.name]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return step.options;
    return step.options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().startsWith(needle),
    );
  }, [query, step.options]);

  const selected = step.options.find((option) => option.value === value) ?? null;

  return (
    <div className={styles.choice}>
      {selected && (
        <p className={styles.detected} role="status">
          Selected: {selected.label} ({selected.value})
        </p>
      )}

      <div className={styles.field}>
        <input
          ref={searchRef}
          className={styles.input}
          value={query}
          aria-labelledby={describedBy}
          autoComplete="off"
          placeholder={step.searchPlaceholder ?? 'Type to filter'}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();

            if (query.trim() === '') {
              if (selected) onPick(selected.value);
              return;
            }

            // Enter takes the best remaining match, which makes the common case
            // — type three letters, press Enter — work without the mouse.
            const best = matches[0];
            if (best) onPick(best.value);
          }}
        />
      </div>

      {matches.length === 0 ? (
        /*
          Why the list came up empty, in the words of the list that did.

          This sentence used to be typed here, naming countries and onboarding,
          in a renderer that knows nothing about either — see `emptyHint` in
          `types.ts`. The country pickers still get that sentence, because it is
          the right one and it is the default; what changed is that they now ask
          for it rather than being the only list this control can serve.
        */
        <p className={styles.hint}>
          {step.emptyHint ??
            'Nothing matches. GOODBUILD can only onboard from the countries listed.'}
        </p>
      ) : (
        <ul className={styles.choiceList} role="listbox" aria-labelledby={describedBy}>
          {matches.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={styles.choiceItem}
                onClick={() => onPick(option.value)}
              >
                <span>{option.label}</span>
                <span className={styles.choiceCode}>{option.value}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
